import React, { useEffect, useState, useMemo } from "react";
import {
  IonContent,
  IonPage,
  IonInput,
  IonButton,
  IonText,
  IonSpinner,
  IonItem,
  IonIcon,
} from "@ionic/react";
import {
  personOutline,
  lockClosedOutline,
  checkmarkCircle,
  ellipseOutline,
  eyeOutline,
  eyeOffOutline,
} from "ionicons/icons";
import { useForm, Controller } from "react-hook-form";
import { useIonRouter } from "@ionic/react";
import { useRegister } from "@/hooks";
import { getErrorMessage } from "@/api/client";
import { registrationStorage } from "@/services/storage";
import "./Auth.css";
import AppLogo from "@components/AppLogo.tsx";

interface OnboardingForm {
  name: string;
  password: string;
  password_confirmation: string;
}

const Onboarding: React.FC = () => {
  const router = useIonRouter();
  const [email, setEmail] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const registerMutation = useRegister();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<OnboardingForm>({
    defaultValues: {
      name: "",
      password: "",
      password_confirmation: "",
    },
  });

  const password = watch("password");

  useEffect(() => {
    // Load email and code from storage/state
    registrationStorage.getEmail().then((storedEmail) => {
      if (storedEmail) {
        setEmail(storedEmail);
        // Get code from URL state or session storage
        const params = new URLSearchParams(window.location.search);
        const codeParam = params.get("code");
        if (codeParam) {
          setCode(codeParam);
        }
      } else {
        // No email stored, go back to welcome
        router.push("/auth/welcome", "back", "replace");
      }
    });
  }, [router]);

  // Store code when navigating from verify-email
  useEffect(() => {
    const storedCode = sessionStorage.getItem("registration_code");
    if (storedCode) {
      setCode(storedCode);
    }
  }, []);

  const passwordRequirements = useMemo(() => {
    return {
      minLength: password.length >= 8,
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /[0-9]/.test(password),
    };
  }, [password]);

  const allRequirementsMet = useMemo(() => {
    return Object.values(passwordRequirements).every(Boolean);
  }, [passwordRequirements]);

  const onSubmit = async (data: OnboardingForm) => {
    if (!email) return;

    // Get code from session storage if not in state
    const finalCode = code || sessionStorage.getItem("registration_code") || "";

    try {
      await registerMutation.mutateAsync({
        email,
        code: finalCode,
        name: data.name,
        password: data.password,
        password_confirmation: data.password_confirmation,
      });
      // Navigation handled by the hook
    } catch {
      // Error handled by mutation
    }
  };

  const handleBack = () => {
    router.push("/auth/verify-email", "back");
  };

  // Store verified code in session for this page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const codeParam = params.get("code");
    if (codeParam) {
      sessionStorage.setItem("registration_code", codeParam);
      setCode(codeParam);
    }
  }, []);

  return (
    <IonPage>
      <IonContent className="auth-content ion-padding">
        <div className="auth-container">
          <div className="auth-header">
            <AppLogo />
            <IonText color="dark">
              <h1>Maak je account</h1>
              <p>Vul je gegevens in om te registreren</p>
            </IonText>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
            <IonItem className="auth-input" lines="none">
              <IonIcon icon={personOutline} slot="start" />
              <Controller
                name="name"
                control={control}
                rules={{
                  required: "Naam is verplicht",
                  minLength: {
                    value: 2,
                    message: "Naam moet minimaal 2 karakters zijn",
                  },
                }}
                render={({ field }) => (
                  <IonInput
                    {...field}
                    type="text"
                    placeholder="Volledige naam"
                    autocomplete="name"
                    onIonInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
            </IonItem>
            {errors.name && (
              <IonText color="danger" className="error-text">
                <p>{errors.name.message}</p>
              </IonText>
            )}

            <IonItem className="auth-input" lines="none">
              <IonIcon icon={lockClosedOutline} slot="start" />
              <Controller
                name="password"
                control={control}
                rules={{
                  required: "Wachtwoord is verplicht",
                  minLength: {
                    value: 8,
                    message: "Wachtwoord moet minimaal 8 karakters zijn",
                  },
                  validate: {
                    hasLowercase: (v) =>
                      /[a-z]/.test(v) || "Minimaal 1 kleine letter vereist",
                    hasUppercase: (v) =>
                      /[A-Z]/.test(v) || "Minimaal 1 hoofdletter vereist",
                    hasNumber: (v) =>
                      /[0-9]/.test(v) || "Minimaal 1 cijfer vereist",
                  },
                }}
                render={({ field }) => (
                  <IonInput
                    {...field}
                    type={showPassword ? "text" : "password"}
                    placeholder="Wachtwoord"
                    autocomplete="new-password"
                    onIonInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
              <IonIcon
                icon={showPassword ? eyeOffOutline : eyeOutline}
                slot="end"
                onClick={() => setShowPassword(!showPassword)}
                style={{ cursor: "pointer" }}
              />
            </IonItem>

            <div className="password-requirements">
              <h4>Wachtwoord vereisten:</h4>
              <div
                className={`password-requirement ${
                  passwordRequirements.minLength ? "met" : ""
                }`}
              >
                <IonIcon
                  icon={
                    passwordRequirements.minLength
                      ? checkmarkCircle
                      : ellipseOutline
                  }
                />
                <span>Minimaal 8 karakters</span>
              </div>
              <div
                className={`password-requirement ${
                  passwordRequirements.hasLowercase ? "met" : ""
                }`}
              >
                <IonIcon
                  icon={
                    passwordRequirements.hasLowercase
                      ? checkmarkCircle
                      : ellipseOutline
                  }
                />
                <span>Minimaal 1 kleine letter</span>
              </div>
              <div
                className={`password-requirement ${
                  passwordRequirements.hasUppercase ? "met" : ""
                }`}
              >
                <IonIcon
                  icon={
                    passwordRequirements.hasUppercase
                      ? checkmarkCircle
                      : ellipseOutline
                  }
                />
                <span>Minimaal 1 hoofdletter</span>
              </div>
              <div
                className={`password-requirement ${
                  passwordRequirements.hasNumber ? "met" : ""
                }`}
              >
                <IonIcon
                  icon={
                    passwordRequirements.hasNumber
                      ? checkmarkCircle
                      : ellipseOutline
                  }
                />
                <span>Minimaal 1 cijfer</span>
              </div>
            </div>

            <IonItem className="auth-input" lines="none">
              <IonIcon icon={lockClosedOutline} slot="start" />
              <Controller
                name="password_confirmation"
                control={control}
                rules={{
                  required: "Bevestig je wachtwoord",
                  validate: (value) =>
                    value === password || "Wachtwoorden komen niet overeen",
                }}
                render={({ field }) => (
                  <IonInput
                    {...field}
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Bevestig wachtwoord"
                    autocomplete="new-password"
                    onIonInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
              <IonIcon
                icon={showConfirmPassword ? eyeOffOutline : eyeOutline}
                slot="end"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{ cursor: "pointer" }}
              />
            </IonItem>
            {errors.password_confirmation && (
              <IonText color="danger" className="error-text">
                <p>{errors.password_confirmation.message}</p>
              </IonText>
            )}

            {registerMutation.error && (
              <IonText color="danger" className="error-text">
                <p>{getErrorMessage(registerMutation.error)}</p>
              </IonText>
            )}

            <IonButton
              expand="block"
              type="submit"
              className="auth-button"
              disabled={!allRequirementsMet || registerMutation.isPending}
            >
              {registerMutation.isPending ? (
                <>
                  <IonSpinner name="crescent" />
                  <span style={{ marginLeft: "8px" }}>Account aanmaken...</span>
                </>
              ) : (
                "Account aanmaken"
              )}
            </IonButton>

            <div className="auth-link">
              <IonButton fill="clear" size="small" onClick={handleBack}>
                Terug
              </IonButton>
            </div>
          </form>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Onboarding;
